// @flow
import _debug from 'debug';
import { uniqBy } from 'lodash';
import { reduce } from '../helpers/utils';
import ApiClient from '../api/Api';
// eslint-disable-next-line import/no-cycle
import type { State as AuthState, User } from './auth';

const debug = _debug('jp:reducers:consents');

const GET_CONSENTS = 'consents/GET_CONSENTS';
const GET_CONSENTS_SUCCESS = 'consents/GET_CONSENTS_SUCCESS';
const GET_CONSENTS_FAIL = 'consents/GET_CONSENTS_FAIL';
const SET_CONSENTS = 'consents/SET_CONSENTS';
const SET_CONSENTS_SUCCESS = 'consents/SET_CONSENTS_SUCCESS';
const SET_CONSENTS_FAIL = 'consents/SET_CONSENTS_FAIL';
const TOGGLE_CONSENT = 'consents/TOGGLE_CURRENT_CONSENT';
const REVOKE = 'consents/DELETE_ACCOUNT';
const REVOKE_SUCCESS = 'consents/DELETE_ACCOUNT_SUCCESS';
const REVOKE_FAIL = 'consents/DELETE_ACCOUNT_FAIL';
const GET_TERMS = 'consents/GET_TERMS';
const GET_TERMS_SUCCESS = 'consents/GET_TERMS_SUCCESS';
const GET_TERMS_FAIL = 'consents/GET_TERMS_FAIL';
const GET_PRIVACY = 'consents/GET_PRIVACY';
const GET_PRIVACY_SUCCESS = 'consents/GET_PRIVACY_SUCCESS';
const GET_PRIVACY_FAIL = 'consents/GET_PRIVACY_FAIL';
const SET_WARNING = 'consents/SET_WARNING';
const SET_MODAL_WARNING = 'consents/SET_MODAL_WARNING';
const REDIRECTING = 'consents/REDIRECTING';

declare var __CLIENT__: boolean;

const MARKETING_CONSENT = 'marketing';

type Consent = {|
  name: string,
  accepted: boolean,
  body: string
|};

export type State = {|
  loaded: boolean,
  loading: boolean,
  consented: Array<Consent>,
  declined: Array<Consent>,
  pending: Array<Consent>,
  consentsSaved: boolean,
  intro: string,
  footer: string,
  warning: string,
  modalWarning: string,
  deleted: boolean,
  revokeError: string,
  redirecting: string,
  terms: any,
  privacy: any
|};

export type Action =
  | {| type: typeof GET_CONSENTS |}
  | {| type: typeof GET_CONSENTS_SUCCESS, payload: any, params: any |}
  | {| type: typeof GET_CONSENTS_FAIL |}
  | {| type: typeof SET_CONSENTS |}
  | {| type: typeof SET_CONSENTS_SUCCESS |}
  | {| type: typeof SET_CONSENTS_FAIL |}
  | {| type: typeof TOGGLE_CONSENT, payload: string |}
  | {| type: typeof REVOKE |}
  | {| type: typeof REVOKE_SUCCESS |}
  | {| type: typeof REVOKE_FAIL, payload: { meta: { user: string } } |}
  | {| type: typeof GET_TERMS_SUCCESS, payload: string |}
  | {| type: typeof GET_PRIVACY_SUCCESS, payload: string |}
  | {| type: typeof SET_WARNING, payload: string |}
  | {| type: typeof SET_MODAL_WARNING, payload: string |}
  | {| type: typeof REDIRECTING, payload: string |};

export type ClientAction = {|
  types: Array<string>,
  params?: any,
  promise: ApiClient => Promise<{}>
|};

const initialState: State = {
  loaded: false,
  loading: false,
  consentsSaved: false,
  consented: [],
  declined: [],
  pending: [],
  intro: '',
  footer: '',
  warning: '',
  modalWarning: '',
  deleted: false,
  revokeError: '',
  redirecting: '',
  terms: {
    loading: false,
    loaded: false,
    content: ''
  },
  privacy: {
    loading: false,
    loaded: false,
    content: ''
  }
};

export default reduce<State, Action>(initialState, {
  [REDIRECTING](state: State, action: Action): State {
    debug(REDIRECTING);
    if (action.type === REDIRECTING) {
      return {
        ...state,
        redirecting: action.payload
      };
    }
    throw new Error('impossible');
  },
  [SET_MODAL_WARNING](state: State, action: Action): State {
    debug(SET_MODAL_WARNING);
    if (action.type === SET_MODAL_WARNING) {
      return {
        ...state,
        modalWarning: action.payload
      };
    }
    throw new Error('impossible');
  },
  [SET_WARNING](state: State, action: Action): State {
    debug(SET_WARNING);
    if (action.type === SET_WARNING) {
      return {
        ...state,
        warning: action.payload
      };
    }
    throw new Error('impossible');
  },
  [GET_CONSENTS](state: State): State {
    debug(GET_CONSENTS);
    return {
      ...state,
      loading: true,
      loaded: false,
      consented: [],
      declined: [],
      pending: []
    };
  },
  [GET_CONSENTS_SUCCESS](state: State, action: Action): State {
    debug(GET_CONSENTS_SUCCESS);
    if (action.type === GET_CONSENTS_SUCCESS) {
      const paramConsent = action.params.consent || [];
      const {
        params: { type }
      } = action;
      let requested =
        typeof paramConsent === 'string' ? [paramConsent] : paramConsent;
      const consents = action.payload.data;
      const update: State = {
        ...state,
        loading: false,
        loaded: true,
        consentsSaved: state.consented.length !== 0,
        intro: consents.intro.data[0].body,
        footer: consents.footer.data[0].body,
        pending: consents.pending.data,
        declined: consents.declined.data,
        consented: consents.consented.data.map(consent => ({
          ...consent,
          accepted: true
        }))
      };
      if (type === 'add-listing') {
        requested = requested.concat(['space_owner']);
      }
      if (requested.length > 0) {
        return {
          ...update,
          pending: consents.pending.data.filter(({ name }) =>
            requested.includes(name)
          ),
          consented: [],
          declined: []
        };
      }
      return (update: State);
    }
    throw new Error('impossible');
  },
  [GET_CONSENTS_FAIL](state: State, action: Action): State {
    debug(GET_CONSENTS_FAIL, action);
    return { ...state, loaded: false, loading: false };
  },
  [SET_CONSENTS](state: State): State {
    debug(SET_CONSENTS);
    return {
      ...state,
      loading: true,
      loaded: false
    };
  },
  [SET_CONSENTS_SUCCESS](state: State): State {
    debug(SET_CONSENTS_SUCCESS);
    return {
      ...state,
      consentsSaved: true,
      loading: false
    };
  },
  [SET_CONSENTS_FAIL](state: State, action: Action): State {
    debug(SET_CONSENTS_FAIL, action);
    return { ...state, loading: false };
  },
  [REVOKE](state: State): State {
    debug(REVOKE);
    return { ...state, loading: true };
  },
  [REVOKE_SUCCESS](): State {
    debug(REVOKE_SUCCESS);
    return {
      ...initialState,
      deleted: true,
      loading: false
    };
  },
  [REVOKE_FAIL](state: State, action: Action): State {
    debug(REVOKE_FAIL, action);
    if (action.type === REVOKE_FAIL) {
      return {
        ...state,
        loading: false,
        revokeError: action.payload ? action.payload.meta.user : ''
      };
    }
    throw new Error('impossible');
  },
  [GET_TERMS](state: State): State {
    debug(GET_TERMS);
    return {
      ...state,
      terms: {
        ...state.terms,
        loading: true
      }
    };
  },
  [GET_TERMS_SUCCESS](state: State, action: Action): State {
    debug(GET_TERMS_SUCCESS);

    if (action.type === GET_TERMS_SUCCESS) {
      return {
        ...state,
        terms: {
          ...state.terms,
          loading: false,
          loaded: true,
          content: action.payload
        }
      };
    }
    throw new Error('impossible');
  },
  [GET_TERMS_FAIL](state: State): State {
    debug(GET_TERMS_FAIL);
    return {
      ...state,
      terms: {
        ...state.terms,
        loading: false,
        loaded: false
      }
    };
  },
  [GET_PRIVACY](state: State): State {
    debug(GET_PRIVACY);
    return {
      ...state,
      privacy: {
        ...state.privacy,
        loading: true
      }
    };
  },
  [GET_PRIVACY_SUCCESS](state: State, action: Action): State {
    debug(GET_PRIVACY_SUCCESS);

    if (action.type === GET_PRIVACY_SUCCESS) {
      return {
        ...state,
        privacy: {
          ...state.privacy,
          loading: false,
          loaded: true,
          content: action.payload
        }
      };
    }
    throw new Error('impossible');
  },
  [GET_PRIVACY_FAIL](state: State): State {
    debug(GET_PRIVACY_FAIL);
    return {
      ...state,
      privacy: {
        ...state.privacy,
        loading: false,
        loaded: false
      }
    };
  },
  [TOGGLE_CONSENT](state: State, action: Action): State {
    debug(TOGGLE_CONSENT, action);
    if (action.type === TOGGLE_CONSENT) {
      const n = action.payload;
      const pending = state.pending.filter(({ name }) => name === n)[0];
      const declinedC = state.declined.filter(({ name }) => name === n)[0];
      const consented = state.consented.filter(({ name }) => name === n)[0];
      if (pending) {
        return {
          ...state,
          pending: state.pending.filter(({ name }) => name !== n),
          consented: state.consented.concat([
            {
              ...pending,
              accepted: true
            }
          ])
        };
      }
      if (declinedC) {
        const { accepted, ...consent } = declinedC;
        return {
          ...state,
          declined: state.declined.filter(({ name }) => name !== n),
          consented: state.consented.concat([
            {
              ...consent,
              accepted: true
            }
          ])
        };
      }
      const { accepted, ...consent } = consented;
      return {
        ...state,
        consented: state.consented.filter(({ name }) => name !== n),
        declined: state.declined.concat([
          {
            ...consent,
            accepted: false
          }
        ])
      };
    }
    throw new Error('impossible');
  }
});

function afterConsents(
  response: any,
  params: any,
  dispatch: any => void,
  match?: { url: string }
): string {
  const approved = response.data ? response.data.consented.data : [];
  const { consent, next, type } = params;
  const requested = (
    (typeof consent === 'string' ? [consent] : consent) || ['standard']
  ).concat(
    type === 'add-listing' && !consent.includes('space_owner')
      ? ['space_owner']
      : []
  );
  if (
    approved.filter(({ name }) => requested.includes(name)).length ===
    requested.length
  ) {
    if (match && match.url.includes('dashboard')) {
      return '';
    }
    if (__CLIENT__) {
      if (window.location.pathname.includes('dashboard')) {
        return '';
      }
      const redirect = next || `${window.location.origin}/dashboard`;
      if (
        process.env.NODE_ENV !== 'test' &&
        params.type !== 'purchase-intent'
      ) {
        // no location support in jsdom :/
        window.location = redirect;
      }
    }
    dispatch({ type: REDIRECTING, payload: next || '/dashboard' });
  }
  return '';
}

export function getConsents(
  params: any = {},
  refresh: boolean,
  match?: { url: string },
  user?: User
): (
  (ClientAction) => *,
  () => { consents: State, auth: AuthState }
) => Promise<any> {
  return (dispatch, getStore) => {
    const state = getStore().consents;
    const { isSpaceOwner } = getStore().auth.user || user || {};
    const { consent, type } = params;
    const shouldFilterListing =
      !consent && !type && !(match || { url: '' }).url.includes('dashboard');
    const filterListing = response =>
      !shouldFilterListing
        ? response
        : {
            data: {
              ...response.data,
              pending: {
                data:
                  isSpaceOwner && !getStore().auth.newUser
                    ? response.data.pending.data
                    : response.data.pending.data.filter(
                        ({ name }) => name !== 'space_owner'
                      )
              }
            }
          };
    if ((!state.loading && !state.loaded) || refresh) {
      return dispatch({
        types: [GET_CONSENTS, GET_CONSENTS_SUCCESS, GET_CONSENTS_FAIL],
        params,
        promise: client =>
          client.consents.get().then(response => {
            const result = filterListing(response);
            afterConsents(result, params, dispatch, match);
            return result;
          })
      });
    }
    const response = filterListing({
      data: {
        intro: { data: [{ name: 'intro', body: state.intro }] },
        pending: { data: state.pending },
        declined: { data: state.declined },
        consented: { data: state.consented },
        footer: { data: [{ name: 'footer', body: state.footer }] }
      }
    });
    afterConsents(response, params, dispatch, match);
    return Promise.resolve(response);
  };
}

export function revoke(params: { next: ?string }) {
  return {
    types: [REVOKE, REVOKE_SUCCESS, REVOKE_FAIL],
    promise: (client: typeof ApiClient) =>
      client.consents.deleteAccount().then(response => {
        if (__CLIENT__ && window.location.pathname.includes('revoke')) {
          const { next } = params;
          const redirect = next || `${window.location.origin}/`;
          window.location = redirect;
        }
        return response;
      })
  };
}

export function terms() {
  return {
    types: [GET_TERMS, GET_TERMS_SUCCESS, GET_TERMS_FAIL],
    promise: (client: typeof ApiClient) => client.consents.getTerms()
  };
}

export function privacy() {
  return {
    types: [GET_PRIVACY, GET_PRIVACY_SUCCESS, GET_PRIVACY_FAIL],
    promise: (client: typeof ApiClient) => client.consents.getPrivacy()
  };
}

export function revokeSpaceOwner() {
  return (dispatch: (*) => *, getState: () => { consents: State }) => {
    const data = getState()
      .consents.pending.filter(({ name }: Consent) => name === 'space_owner')
      .map((consent: Consent) => ({ accepted: false, ...consent }));
    return dispatch({
      types: [SET_CONSENTS, SET_CONSENTS_SUCCESS, SET_CONSENTS_FAIL],
      promise: (client: typeof ApiClient) =>
        client.consents.create(data).then(() => dispatch(getConsents({}, true)))
    });
  };
}

export function setMarketingConsent(accepted: boolean) {
  return (dispatch: (*) => *, getState: () => { consents: State }) => {
    const data = getState()
      .consents.pending.filter(
        ({ name }: Consent) => name === MARKETING_CONSENT
      )
      .map((consent: Consent) => ({ accepted, ...consent }));

    return dispatch({
      types: [SET_CONSENTS, SET_CONSENTS_SUCCESS, SET_CONSENTS_FAIL],
      promise: (client: typeof ApiClient) =>
        client.consents.create(data).then(() => dispatch(getConsents({}, true)))
    });
  };
}

export function setConsents(
  force: boolean,
  match?: { url: string },
  params?: any
) {
  return (
    dispatch: (*) => *,
    getState: () => { consents: State, auth: AuthState }
  ) => {
    const pending = getState().consents.pending.map(({ name }) => name);
    const consented = getState().consents.consented.map(({ name }) => name);
    const declined = getState().consents.declined.map(({ name }) => name);
    if (
      !force &&
      (pending.includes('space_owner') || declined.includes('space_owner')) &&
      consented.includes('standard') &&
      (getState().auth.user || {}).isSpaceOwner
    ) {
      return dispatch({
        type: SET_MODAL_WARNING,
        payload:
          'You will lose any listing data you have saved unless you consent to keeping it.'
      });
    }
    if (!force && !consented.includes('standard')) {
      return dispatch({
        type: SET_WARNING,
        payload:
          "You must either tick the 'Standard' checkbox or 'I disagree with all of the above...' link below"
      });
    }
    const data = uniqBy(
      getState()
        .consents.consented.concat(
          getState().consents.declined.map(({ accepted, ...consent }) => ({
            ...consent,
            accepted: false
          }))
        )
        .concat(
          match && match.url.includes('dashboard')
            ? getState().consents.pending.map(({ accepted, ...consent }) => ({
                ...consent,
                accepted: false
              }))
            : []
        ),
      ({ name }) => name
    );
    return dispatch({
      types: [SET_CONSENTS, SET_CONSENTS_SUCCESS, SET_CONSENTS_FAIL],
      promise: (client: typeof ApiClient) =>
        client.consents.create(data).then(() => {
          if (match && match.url.includes('dashboard') && __CLIENT__) {
            window.location = `${window.location.origin}/dashboard`;
          } else {
            dispatch(
              getConsents(
                { ...params }, // TODO: needs a destruct as there is a bug with encoded next urls
                true,
                match,
                getState().auth.user || undefined
              )
            );
          }
        })
    });
  };
}

export function toggleConsent(name: string): Action {
  return {
    type: TOGGLE_CONSENT,
    payload: name
  };
}

export function cancelWarning(): Action {
  return {
    type: SET_MODAL_WARNING,
    payload: ''
  };
}

export const analyticsEventSubscribers = {
  [SET_CONSENTS_SUCCESS]: (
    action: Action,
    analytics: any,
    getState: () => any
  ) => {
    const state = getState();
    analytics.track('consents:success', {
      newUser: state.auth.newUser,
      marketing:
        state.consents.consented
          .map(consent => consent.name === MARKETING_CONSENT)
          .filter(b => b).length > 0
    });
  },
  [SET_CONSENTS_FAIL]: (action: Action, analytics: any) => {
    analytics.track('consents:fail', action);
  }
};

export const selectConsentsHasLoaded = (state: { consents: State }) =>
  state.consents.loaded;

export const selectConsentsShouldAskForMarketingConsent = (state: {
  consents: State
}) => {
  // Declined consents appear in both pending and declined.
  // Check marketing is pending and not declined
  return (
    state.consents.pending.find(
      (item: Consent) => item.name === MARKETING_CONSENT
    ) !== undefined &&
    state.consents.declined.find(
      (item: Consent) => item.name === MARKETING_CONSENT
    ) === undefined
  );
};

export const selectConsentsHasOptedInToMarketingConsent = (state: {
  consents: State
}) => {
  return (
    state.consents.consented
      .map(consent => consent.name === MARKETING_CONSENT)
      .filter(b => b).length > 0
  );
};
